import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, Switch } from '@components/Common';
import { FilterContainer } from '@components/Common/EntryFilters';
import DateFilterControlled from '@components/Common/EntryFilters/DateFilterControlled';
import TankSelectorControlled from '@components/Common/EntryFilters/TankSelectorControlled';
import FormLoader from '@components/Common/Loaders/FormLoader';
import NavBar from '@components/Pages/Reports/Common/ReportNavigation';
import { useTerminalsQuery } from '@api/queries/terminals';
import { useTanksQuery } from '@api/queries/tanks';
import { useDownloadTankReportMutation } from '@api/queries/reports';
import { useTankReportFilters } from '@hooks/useTankReportFilters';

// #region styles

const Columns = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;

  > div.column {
    box-sizing: border-box;
    width: 25%;
    padding-right: 15px;

    &.wide {
      flex-grow: 1;
    }

    &:last-child {
      padding-right: 0;
    }
  }
`;

const CreateReportButton = styled(Button)`
  display: inline-block;
  padding: 10px 35px;
  text-decoration: none;

  border-right-width: 0;
  background: rgb(5, 195, 222);
  color: rgba(255, 255, 255);

  margin-left: 20px;
  margin-top: 40px;
  margin-bottom: 15px;
  border-right-width: 1px;
`;

const StyledSwitch = styled(Switch)`
  &:not(:first-child) {
    margin-left: 35px;
  }
`;

// #endregion

const TanksReporting: React.FC = () => {
  const { t } = useTranslation();

  // URL-based filters
  const {
    filters,
    tankFilter,
    dateFilter,
    setTankFilter,
    setDateFilter,
    setGroupByReference,
    setGroupByLoading,
    setIncludeInvoicingInfo,
  } = useTankReportFilters();

  // Fetch reference data using TanStack Query
  const { data: terminals = [] } = useTerminalsQuery();
  const { data: tanks = [] } = useTanksQuery();

  // Report download mutation
  const downloadMutation = useDownloadTankReportMutation();

  const isLoading = downloadMutation.isPending;

  const createReport = useCallback(() => {
    downloadMutation.mutate({
      tankId: filters.tankId,
      from: filters.startDate,
      to: filters.endDate,
      groupByReference: filters.groupByReference,
      groupByLoading: filters.groupByLoading,
      includeInvoicingInfo: filters.includeInvoicingInfo,
    });
  }, [downloadMutation, filters]);

  const isReportCreationDisabled = !filters.tankId || !filters.startDate || !filters.endDate;

  return (
    <>
      <NavBar />
      <FilterContainer>
        <Columns>
          <TankSelectorControlled
            value={tankFilter}
            onChange={setTankFilter}
            terminals={terminals}
            tanks={tanks}
          />
          <DateFilterControlled value={dateFilter} onChange={setDateFilter} />

          {isLoading && <FormLoader title={t('reports.loading')} />}
          <CreateReportButton onClick={createReport} disabled={isReportCreationDisabled}>
            {t('reports.create_report')}
          </CreateReportButton>
        </Columns>
        <Columns>
          <StyledSwitch
            label={t('reports.select_reference_grouping')}
            onChange={(i: boolean) => setGroupByReference(i)}
            checked={filters.groupByReference}
          />
          <StyledSwitch
            label={t('reports.select_loading_grouping')}
            onChange={(i: boolean) => setGroupByLoading(i)}
            checked={filters.groupByLoading}
          />
          <StyledSwitch
            label={t('reports.include_invoicing')}
            onChange={(i: boolean) => setIncludeInvoicingInfo(i)}
            checked={filters.includeInvoicingInfo}
          />
        </Columns>
      </FilterContainer>
    </>
  );
};

export default TanksReporting;
